<template>
  <div>
    <v-card>
      <div class="d-flex align-center justify-space-between">
        <v-card-title> Statements Audit Report </v-card-title>
      </div>
      <v-container>
        <v-form>
          <v-row>
            <v-col
              cols="12"
              md="4"
            >
              <v-text-field
                v-model="fromDate"
                required
                label="From Date"
                type="date"
                :error-messages="fromDateErrors"
                @input="fromDateOnInputOrBlur"
                @blur="fromDateOnInputOrBlur"
              />
            </v-col>
            <v-col
              cols="12"
              md="4"
            >
              <v-text-field
                v-model="toDate"
                required
                label="To Date"
                type="date"
                :error-messages="toDateErrors"
                @input="toDateOnInputOrBlur"
                @blur="toDateOnInputOrBlur"
              />
            </v-col>
          </v-row>
          <v-row>
            <v-col
              cols="12"
              md="4"
            >
              <v-select
                v-model="isMissing"
                :items="missingOptions"
                item-title="text"
                item-value="value"
                label="Is Missing"
              />
            </v-col>
          </v-row>
          <v-btn
            class="mt-5 mb-5"
            color="blue"
            :loading="reportGenerating"
            @click="generateReport()"
          >
            Generate Report
          </v-btn>
        </v-form>
      </v-container>
    </v-card>

    <template v-if="reportLoaded">
      <v-data-table
        dense
        :headers="headers"
        :items="items"
        class="elevation-1 mt-2"
      >
        <template #item.period_start="{ item }">
          {{ formatDate(item.period_start) }}
        </template>

        <template #item.period_end="{ item }">
          {{ formatDate(item.period_end) }}
        </template>

        <template #item.missing="{ item }">
          {{ item.missing ? 'Yes' : 'No' }}
        </template>
      </v-data-table>
    </template>
  </div>
</template>

<script>
import { useVuelidate } from '@vuelidate/core';
import { required } from '@vuelidate/validators';
import { DateTime, Interval } from 'luxon';

const lessThanToDate = (value, vm) => (value <= vm.toDate);
const greaterThanFromDate = (value, vm) => (value >= vm.fromDate);

export default {
  name: 'StatementsAuditReport',
  setup() { return { v$: useVuelidate() }; },
  data() {
    return {
      formProcessing: false,
      fromDate: DateTime.local().toISODate(),
      toDate: DateTime.local().toISODate(),
      reportLoaded: false,
      reportGenerating: false,
      headers: [
        { title: 'Loan ID', key: 'loan_id' },
        { title: 'Period Start', key: 'period_start' },
        { title: 'Period End', key: 'period_end' },
        { title: 'Missing', key: 'missing' },
      ],
      items: [],
      isMissing: null,
      missingOptions: [
        {
          value: null,
          text: 'All',
        },
        {
          value: true,
          text: 'Yes',
        },
        {
          value: false,
          text: 'No',
        },
      ],
    };
  },
  computed: {
    fromDateErrors() {
      const errors = [];

      if (!this.v$.fromDate.$dirty) {
        return errors;
      }
      if (!this.v$.fromDate.required) {
        errors.push('From Date is required.');
      }

      return errors;
    },
    toDateErrors() {
      const errors = [];

      if (!this.v$.toDate.$dirty) {
        return errors;
      }
      if (!this.v$.toDate.required) {
        errors.push('To Date is required.');
      }

      return errors;
    },
  },
  created() {
    const routeParams = this.$route.params;
    let generateReport = false;

    if (routeParams.fromDate) {
      this.fromDate = routeParams.fromDate;
      generateReport = true;
    }

    if (routeParams.toDate) {
      this.toDate = routeParams.toDate;
      generateReport = true;
    }

    if (generateReport) {
      this.generateReport();
    }
  },
  methods: {
    generateReport() {
      const vm = this;

      this.v$.$touch();

      if (!this.v$.$invalid) {
        this.reportLoaded = false;
        this.reportGenerating = true;

        this.$axios
          .get('api/v1/reports/statements-audit/', {
            params: {
              fromDate: this.fromDate,
              toDate: this.toDate,
              isMissing: this.isMissing,
            },
          })
          .then((res) => {
            vm.items = Object.values(res.data);
            vm.reportLoaded = true;
          })
          .then(() => {
            vm.reportGenerating = false;
          });
      }
    },
    resetDateValidators() {
      this.v$.fromDate.$reset();
      this.v$.toDate.$reset();
    },
    touchDateValidators() {
      this.v$.fromDate.$touch();
      this.v$.toDate.$touch();
    },
    fromDateOnInputOrBlur() {
      this.reportLoaded = false;
      this.resetDateValidators();
      this.touchDateValidators();

      if (this.fromDate > this.toDate) {
        this.fromDateErrors.push('From Date cannot be after To Date.');
      }
    },
    toDateOnInputOrBlur() {
      this.reportLoaded = false;
      this.resetDateValidators();
      this.touchDateValidators();

      if (this.fromDate > this.toDate) {
        this.toDateErrors.push('To Date cannot be before From Date.');
      }
    },
    diffInDays(date1, date2) {
      const before = (date1 < date2) ? date1 : date2;
      const after = (date1 > date2) ? date1 : date2;
      return Interval.fromDateTimes(DateTime.fromISO(before), DateTime.fromISO(after)).length('days');
    },
    formatDate(date) {
      return DateTime.fromISO(date).toUTC().toLocaleString(DateTime.DATE_MED);
    },
    formatAmount(number) {
      const amount = parseFloat(number).toFixed(2);

      if (amount === '0.00') {
        return '-';
      }

      if (amount < 0) {
        return `-$${Math.abs(amount)}`;
      }

      return `$${amount}`;
    },
  },
  validations: {
    fromDate: { required, lessThanToDate },
    toDate: { required, greaterThanFromDate },
  },
};
</script>

<style></style>
